/**
 * Spanish as spoken in Spain. scope.md §10.
 *
 * Typed against the English dictionary: a key missing here, or one here that English
 * does not have, is a compile error.
 *
 * Vocabulary: a set is a **grupo**, a run an **escalera**, a joker a **comodín**, the
 * face-down pile the **mazo**, and laying down is **bajarse**.
 */

import type { Dictionary } from './index.js';

export const es: Dictionary = {
  // ------------------------------------------------------------------ common
  'common.cancel': 'Cancelar',
  'common.back': 'Atrás',
  'common.done': 'Hecho',
  'common.yes': 'Sí',
  'common.no': 'No',
  'common.you': 'Tú',
  'common.someone': 'Alguien',
  'common.nobody': 'Nadie',
  'common.leave': 'Salir',
  'common.reallyLeave': '¿Seguro que sales?',
  'common.remove': 'Quitar',
  'common.really': '¿Seguro?',
  'common.howToPlay': 'Cómo se juega',
  'common.settings': 'Ajustes',
  'common.waitingFor': 'Esperando a {names}…',
  'common.roundOf': 'Ronda {round} de {rounds}',
  'seconds.one': '{count} segundo',
  'seconds.other': '{count} segundos',
  'seconds.short': '{count} s',

  // ------------------------------------------------------------------ connection
  'offline.message': 'Sin conexión — volviendo a intentarlo…',
  'offline.retry': 'Reintentar',

  // ------------------------------------------------------------------ home
  'home.tagline': 'Siete rondas. Gana quien menos puntos tenga.',
  'home.yourName': 'Tu nombre',
  'home.namePlaceholder': 'Un nombre, o solo una inicial',
  'home.nameNeeded': 'Primero pon un nombre — basta con una inicial. Este dispositivo lo recuerda.',
  'home.wins.one': '{count} partida ganada en este dispositivo',
  'home.wins.other': '{count} partidas ganadas en este dispositivo',
  'home.start': 'Crear partida',
  'home.join': 'Unirse a una partida',
  'home.enterCode': 'Escribe el código de cuatro letras',
  'home.delete': 'Borrar',
  'home.joinButton': 'Entrar',

  // ------------------------------------------------------------------ lobby
  'lobby.roomCode': 'Código de la sala',
  'lobby.invite': 'Invitar jugadores',
  'lobby.players': 'Jugadores ({count})',
  'lobby.offline': 'desconectado',
  'lobby.removePlayer': 'Quitar a {name}',
  'lobby.addBot': 'Añadir un bot',
  'lobby.rules': 'Reglas',
  'lobby.change': 'Cambiar',
  'lobby.howToPlayRules': 'Cómo se juega con estas reglas',
  'lobby.deal': 'Repartir',
  'lobby.waitingForHost': 'Esperando a que el anfitrión reparta…',

  'bot.bumble.blurb': 'Tranquilo, se equivoca',
  'bot.scout.blurb': 'Juega con cabeza',
  'bot.ace.blurb': 'Cuenta las cartas',
  'bot.robo.blurb': 'Implacable',

  'ruleset.house.name': 'Reglas de casa',
  'ruleset.house.description': 'Como se juega en casa.',
  'ruleset.traditional.name': 'Tradicional',
  'ruleset.traditional.description': 'As alto o bajo, tres escaleras de cuatro para terminar y tres peticiones por ronda.',
  'ruleset.gentle.name': 'Suave',
  'ruleset.gentle.description': 'Más fácil para los pequeños: quince segundos para pedir, y pedir es gratis.',
  'ruleset.custom': 'A vuestra manera',

  'rules.ace': 'El as en escaleras',
  'rules.ace.both': 'Alto o bajo',
  'rules.ace.low': 'Solo bajo',
  'rules.ace.high': 'Solo alto',
  'rules.timeToAsk': 'Tiempo para pedir',
  'rules.claimPenalty': 'Pedir cuesta una carta',
  'rules.lastRound': 'Última ronda',
  'rules.lastRound.allCards': 'Todas las cartas',
  'rules.lastRound.threeRunsOf4': '3 escaleras de 4',

  'pack.seaside.name': 'Costa',
  'pack.space.name': 'Cadetes del espacio',
  'pack.woodland.name': 'Bosque',
  'pack.clarity.name': 'Claridad',

  // ------------------------------------------------------------------ invite
  'invite.title': 'Invitar jugadores',
  'invite.subtitle': 'Manda el enlace o deja que escaneen esto.',
  'invite.qrAlt': 'Código QR de la sala {code}',
  'invite.share': 'Compartir enlace',
  'invite.whatsapp': 'WhatsApp',
  'invite.text': 'SMS',
  'invite.copied': 'Enlace copiado',
  'invite.shared': 'Compartido',
  'invite.cancelled': 'No pasa nada — el código está arriba',
  'invite.unavailable': 'Mejor diles el código en voz alta',
  'invite.message': 'Ven a jugar a Garbage conmigo — código de sala {code}',

  // ------------------------------------------------------------------ contracts and cards
  'contract.set.one': '{count} grupo de {length}',
  'contract.set.other': '{count} grupos de {length}',
  'contract.run.one': '{count} escalera de {length}',
  'contract.run.other': '{count} escaleras de {length}',
  'contract.and': ' y ',
  'contract.wholeHand': '{count} escaleras de al menos {length}, usando todas las cartas',
  'meld.set': 'Grupo',
  'meld.run': 'Escalera',
  'meld.set.explained': 'un grupo — mismo número',
  'meld.run.explained': 'una escalera — mismo palo, en orden',
  'card.joker': 'comodín',
  'card.wild': 'JOKER',

  // ------------------------------------------------------------------ briefing and dealing
  'briefing.header': 'Ronda {round} de {rounds} · {cards} cartas cada uno',
  'briefing.youNeed': 'Necesitas {contract}',
  'briefing.youLead': 'Empiezas tú',
  'briefing.leads': 'Empieza {name}',
  'briefing.leadsTag': '(empieza)',
  'briefing.example': 'Algo así',
  'briefing.lastRound': 'Última ronda: tus tres escaleras tienen que usar **todas las cartas**, así que al final no tiras ninguna.',
  'briefing.turnedUp': 'Carta boca arriba',
  'briefing.turnedUpHint': 'Cualquiera puede pedirla en cuanto estéis todos listos.',
  'briefing.yourCards': 'Tus cartas — toca una y muévela con las flechas',
  'briefing.ready': '¡Listo!',
  'briefing.starting': 'Empezando…',
  'dealing.label': 'Repartiendo la ronda {round}',
  'dealing.caption': 'Ronda {round} · {cards} cartas cada uno',

  // ------------------------------------------------------------------ hand and melds
  'hand.move': 'Mover',
  'hand.moveLeft': 'Mover la carta a la izquierda',
  'hand.moveRight': 'Mover la carta a la derecha',
  'hand.empty': 'No tienes cartas.',
  'hand.count.one': '{count} carta',
  'hand.count.other': '{count} cartas',
  'melds.takeJoker': 'coger comodín',
  'melds.addHere': 'añadir aquí',
  'melds.empty': 'Nadie se ha bajado todavía.',

  // ------------------------------------------------------------------ table
  'table.header': 'Ronda {round} de {rounds} · {packs} barajas',
  'table.timeToAsk': 'Tiempo para pedir: {time}',
  'table.timeToAskChange': 'Tiempo para pedir: {time}. Cambiar',
  'table.opponentDown': 'bajado',
  'table.opponentOff': 'fuera',
  'table.opponentAway': 'ausente',
  'table.theyGot': '{name} se lleva el {card}',
  'table.youGot': 'Te llevas el {card}',
  'table.extraCard': '+1 carta más',
  'table.claimTimeChanged': 'Ahora hay {time} para pedir',
  'table.theyThrew': '{name} ha tirado una carta…',
  'table.youThrew': 'Has tirado una carta…',
  'table.upForGrabs': 'Hay una carta para quien la quiera…',
  'table.yourTurnTake': 'Te toca — coge una carta',
  'table.yourTurn': 'Te toca',
  'table.theirTurn': 'Le toca a {name}',
  'table.stock': 'Mazo ({count})',
  'table.throwHere': 'Tírala aquí',
  'table.thrown': 'Tiradas ({count})',
  'table.layDown': 'Bajarse: {contract}',
  'table.ways': '({count} formas)',
  'table.pendingJoker.one': 'Coloca el comodín antes de terminar tu turno.',
  'table.pendingJoker.other': 'Coloca los comodines antes de terminar tu turno.',

  'claim.nextCardDown': 'La carta de debajo',
  'claim.theyThrewIt': 'La ha tirado {name}',
  'claim.youThrewIt': 'La has tirado tú',
  'claim.thrownAway': 'Carta tirada',
  'claim.firstInLine': ' · primero en la cola',
  'claim.nobodyAsked': 'Nadie la ha pedido todavía',
  'claim.mayI': '¡La quiero!',
  'claim.costsCard': '+1 carta más',
  'claim.free': 'gratis — es tu turno, sin esperar',
  'claim.asked': 'Pedida ✓',
  'claim.noAsksLeft': 'No te quedan peticiones',

  'dig.title': '¿Cuántas del montón de descartes?',
  'dig.hint': 'Toca una carta para coger todas hasta ella. Las de castigo salen del mazo, sin verlas.',
  'dig.free': 'Solo la de arriba — gratis',
  'dig.cost.one': '{count} cartas — 1 carta de castigo, tu mano crece en {grows}',
  'dig.cost.other': '{count} cartas — {penalties} cartas de castigo, tu mano crece en {grows}',
  'dig.costFree': '{count} cartas — sin castigo, tu mano crece en {count}',
  'dig.fewer': 'Una menos',
  'dig.more': 'Una más',
  'dig.take.one': 'Coger {count} carta',
  'dig.take.other': 'Coger {count} cartas',
  'dig.limit': 'No puedes coger más',

  'dead.title': 'La ronda se ha atascado',
  'dead.why': 'El mazo está vacío y nadie ha bajado nada en dos vueltas.',
  'dead.end': 'Terminar la ronda',
  'dead.addPack': 'Añadir una baraja',
  'dead.endOnly': 'La baraja extra ya está dentro — solo queda terminar la ronda.',
  'dead.voted': 'Has elegido: {choice}',
  'dead.waiting': 'Esperando a {names}…',
  'dead.rule': 'Solo termina si todos están de acuerdo. Si alguien quiere seguir, se baraja una baraja nueva.',

  'down.pick': 'Elige un grupo o una escalera para bajar',
  'down.pickNext': 'Ahora elige el siguiente',
  'jokerEnd.question': '¿En qué punta va el comodín?',
  'jokerEnd.low': 'Punta baja',
  'jokerEnd.high': 'Punta alta',
  'jokerEnd.as': 'como {card}',
  'kick.label': 'Quitar a {name}',
  'kick.confirm': '¿Seguro que quitas a {name}?',
  'stillThere.question': '¿Sigues ahí?',
  'stillThere.hint': 'Los demás están esperando. Si se acaba el tiempo, sales de la partida.',
  'stillThere.more': '+20 s',

  // ------------------------------------------------------------------ reactions
  'reactions.open': 'Mandar una reacción',
  'reactions.close': 'Cerrar las reacciones',

  // ------------------------------------------------------------------ round and match over
  'result.matchWinner': '¡Gana {name}!',
  'result.wentOut': '{name} ha cerrado',
  'result.ranOut': 'Fin de la ronda — se acabaron las cartas',
  'score.player': 'Jugador',
  'score.thisRound': 'Esta ronda',
  'score.total': 'Total',
  'score.ready': 'Listo',
  'score.notReady': 'Aún no',
  'score.lowestWins': 'Gana quien menos puntos tenga',
  'next.header': 'Siguiente: ronda {round} de {rounds} · {cards} cartas cada uno',
  'roundOver.dealMeIn': '¡A por la siguiente!',
  'matchOver.playAgain': 'Otra partida',
  'matchOver.waitingForHost': 'Esperando a que {name} empiece otra partida…',
  'matchOver.leave': 'Dejar la mesa',
  'matchOver.feedback': 'Cuéntanos qué te ha parecido',

  // ------------------------------------------------------------------ settings and feedback
  'settings.title': 'Ajustes',
  'settings.language': 'Idioma',
  'settings.cardDesign': 'Diseño de cartas · solo para ti',
  'settings.sounds': 'Sonidos',
  'settings.on': 'Sí',
  'settings.off': 'No',
  'settings.soundsHint': 'Un aviso cuando te toca o cuando hay una carta para pedir, y una vibración si el móvil lo permite.',
  'settings.feedback': 'Enviar comentarios',
  'settings.records': 'Las partidas se guardan sin ningún nombre, para ayudarnos a mejorar los bots.',

  'feedback.title': 'Enviar comentarios',
  'feedback.intro': '¿Algo no funciona o tienes una idea? Leemos todos los mensajes.',
  'feedback.kind.bug': 'Algo falla',
  'feedback.kind.idea': 'Una idea',
  'feedback.kind.other': 'Otra cosa',
  'feedback.message': '¿Qué nos quieres contar?',
  'feedback.contact': 'Correo, si quieres respuesta (opcional)',
  'feedback.private': 'No pongas nada privado, por favor.',
  'feedback.send': 'Enviar',
  'feedback.sending': 'Enviando…',
  'feedback.thanks': 'Gracias — ya está enviado.',
  'feedback.failed': 'No se ha podido enviar. Inténtalo otra vez dentro de un momento.',
  'feedback.tooMany': 'Son muchos mensajes seguidos — inténtalo más tarde.',

  // ------------------------------------------------------------------ how to play
  'help.intro': 'Estas son las reglas de esta partida. Puede que no sean las que tú aprendiste.',
  'help.idea.title': 'La idea',
  'help.idea.body': 'Siete rondas. En cada ronda te reparten más cartas y tienes que formar una combinación más difícil. Las cartas que te queden en la mano al final de una ronda cuentan en tu contra, y **gana quien menos puntos tenga después de {rounds} rondas**.',
  'help.melds.title': 'Grupos y escaleras',
  'help.melds.body': 'Un **grupo** son tres o más cartas del mismo número. Una **escalera** son cuatro o más cartas del mismo palo seguidas — **cuatro es siempre el mínimo**.',
  'help.ace.low': 'El as **solo va abajo, así que A-2-3-4 vale y Q-K-A no**. Una escalera nunca da la vuelta por el as, así que K-A-2 no es escalera.',
  'help.ace.high': 'El as **solo va arriba, así que Q-K-A vale y A-2-3-4 no**. Una escalera nunca da la vuelta por el as, así que K-A-2 no es escalera.',
  'help.ace.both': 'El as **va arriba o abajo, así que valen A-2-3-4 y J-Q-K-A**. Una escalera nunca da la vuelta por el as, así que K-A-2 no es escalera.',
  'help.jokers': 'Cada baraja trae **dos comodines**, y un comodín vale por la carta que te haga falta. Un grupo o una escalera tiene que llevar al menos una carta de verdad; por lo demás, úsalos como quieras.',
  'help.rounds.title': 'Las rondas',
  'help.rounds.round': 'Ronda',
  'help.rounds.cards': 'Cartas',
  'help.rounds.need': 'Necesitas',
  'help.rounds.lastRound': 'En la última ronda las tres escaleras usan **todas las cartas de tu mano**, así que al final no se tira ninguna — cierras al bajarlas.',
  'help.turn.title': 'Tu turno',
  'help.turn.body': 'Coge una carta — la de arriba del mazo o la última que se ha tirado. Después bájate con la combinación de la ronda si puedes, añade cartas a lo que ya esté en la mesa y, para terminar, tira una carta.',
  'help.turn.spare': 'De la ronda 1 a la 6 siempre te sobra una carta después de bajarte, así que tienes que colocarla en las cartas de alguien antes de poder cerrar. Es normal.',
  'help.turn.onlyAdd': '**Solo puedes añadir a lo que ya está en la mesa.** Una vez que te has bajado no puedes empezar un grupo o una escalera nuevos, por buenos que sean — tres reyes en la mano se quedan en la mano, y cuentan en tu contra, salvo que alguien tenga reyes bajados.',
  'help.ask.title': '«¡La quiero!»',
  'help.ask.body': 'Cuando alguien tira una carta, los demás tenéis **{time}** para pedirla. El anfitrión puede alargar o acortar ese tiempo cuando quiera con el botón ⏱.',
  'help.ask.penalty': 'A quien le toca después no le cuesta nada — es su carta para ese turno. Cualquier otro que la pida coge además una carta del mazo, así que su mano crece en dos.',
  'help.ask.free': 'A quien le toca después no le cuesta nada — es su carta para ese turno. En esta partida pedir no cuesta nada más.',
  'help.ask.limit': 'Solo puedes pedir **{count}** veces por ronda.',
  'help.ask.cascade': 'Cuando alguien se lleva la carta tirada **fuera de su turno**, queda al descubierto la de debajo y todos tenéis otros **{time}** para pedirla — y así hacia abajo por las cartas tiradas, hasta que nadie quiera la que se ve. Solo entonces sigue el juego.',
  'help.ask.anybody': 'Cualquiera puede pedir una carta que ha quedado al descubierto — incluso quien acaba de tirar, e incluso una carta que tiró él mismo en un turno anterior.',
  'help.ask.pileOnly': 'Esto solo pasa con las cartas tiradas. El mazo sigue boca abajo y se coge de una en una, en tu propio turno.',
  'help.joker.title': 'Coger un comodín',
  'help.joker.any': 'Una vez que te has bajado, puedes coger un comodín de las cartas de cualquiera en la mesa dando la carta de verdad a la que sustituye. Tienes que usar ese comodín en el mismo turno.',
  'help.joker.othersOnly': 'Una vez que te has bajado, puedes coger un comodín de las cartas de otro jugador en la mesa dando la carta de verdad a la que sustituye. Tienes que usar ese comodín en el mismo turno.',
  'help.scoring.title': 'Puntos',
  'help.scoring.low': 'Del 2 al 9: **{points}** cada una',
  'help.scoring.ten': '10, J, Q, K: **{points}** cada una',
  'help.scoring.ace': 'As: **{points}**',
  'help.scoring.joker': 'Comodín: **{points}**',
  'help.bonus.finalRound': 'Cerrar en la última ronda resta 50.',
  'help.bonus.everyRound': 'Cerrar resta 50, en todas las rondas.',
  'help.bonus.none': 'Cerrar no da ningún premio.',
  'help.scoring.lowest': 'Gana quien menos puntos tenga después de siete rondas.',
  'help.gotIt': 'Entendido',

  // ------------------------------------------------------------------ what the server may refuse
  'serverError.messageTooLarge': 'El mensaje es demasiado grande',
  'serverError.malformed': 'Mensaje no válido',
  'serverError.invalidMessage': 'Mensaje no válido',
  'serverError.unknownMessage': 'Mensaje desconocido',
  'serverError.alreadyInGame': 'Ya estás en una partida',
  'serverError.noSuchGame': 'No hay ninguna partida con ese código',
  'serverError.joinFirst': 'Primero únete a una partida',
  'serverError.gameFull': 'La partida está llena',
  'serverError.alreadyStarted': 'La partida ya ha empezado',
  'serverError.seatGone': 'Ese sitio ya no está disponible',
  'serverError.hostOnlyRules': 'Solo el anfitrión puede cambiar las reglas',
  'serverError.rulesLocked': 'Las reglas no se pueden cambiar una vez repartido',
  'serverError.hostOnlyClaimTime': 'Solo el anfitrión puede cambiar el tiempo para pedir',
  'serverError.hostOnlyDeal': 'Solo el anfitrión puede repartir',
  'serverError.hostOnlyPlayAgain': 'Solo el anfitrión puede empezar otra partida',
  'serverError.hostOnlyKick': 'Solo el anfitrión puede quitar a un jugador',
  'serverError.nothingToReadyFor': 'Todavía no hay nada para lo que estar listo',
  'serverError.notInGame': 'No estás en esta partida',
  'serverError.twoPlayersNeeded': 'Hacen falta dos jugadores',
  'serverError.roundInProgress': 'Ya hay una ronda en juego',
  'serverError.matchOver': 'La partida ha terminado',
  'serverError.matchNotOver': 'La partida todavía no ha terminado',
  'serverError.noGame': 'No hay ninguna partida en juego',
  'serverError.noClock': 'No tienes ningún reloj en marcha',
  'serverError.kickTooSoon': 'Solo se puede quitar a alguien que lleve dos minutos ausente',
  'serverError.couldNotRemove': 'No se ha podido quitar a ese jugador',
  'serverError.tooManyReactions': 'Un poco más despacio',
  'serverError.notAllowed': 'Eso no se puede hacer ahora',
  'serverError.roomClosedEmpty': 'La sala se ha cerrado porque estaba vacía',
  'serverError.timedOut': 'Se te acabó el tiempo, así que has salido de la partida',
};
